import { useEffect, useState } from 'react';
import { Car, Layers, Link2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchEngins, deleteEngin } from '@/store/thunks/enginThunks';
import { fetchEnginAffectations } from '@/store/thunks/enginAffectationThunks';
import type { Engin } from '@/types';
import { checkPermission } from '@/utils/checkPermission';
import { filterListByQuery } from '@/utils/filterList';
import Page from '@/components/Page';
import { AddButton, FilterInput, Modal, DataTable, ConfirmModal, Tabs, TabPanel, ListWithPagination, TableActions } from '@/components/ui';
import EnginForm from '@/components/engin/EnginForm';
import EnginFamilleTab from '@/components/engin/EnginFamilleTab';
import EnginAffectationsSection from '@/components/engin/EnginAffectationsSection';

export default function EnginPage() {
  const dispatch = useAppDispatch();
  const { engins, pageNum, totalPages } = useAppSelector((s: RootState) => s.engin);
  const { isAdmin, authorities } = useAppSelector((s: RootState) => s.auth);
  const [tab, setTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Engin | null>(null);
  const [toDelete, setToDelete] = useState<Engin | null>(null);
  const [filterQuery, setFilterQuery] = useState('');

  useEffect(() => {
    dispatch(fetchEngins(0));
  }, [dispatch]);

  useEffect(() => {
    if (tab === 2) dispatch(fetchEnginAffectations(0));
  }, [tab, dispatch]);

  const canCreate = isAdmin || checkPermission('CREE_ENGINE', authorities);
  const canUpdate = isAdmin || checkPermission('UPDATE_ENGINE', authorities);
  const canDelete = isAdmin || checkPermission('DELETE_ENGINE', authorities);

  const filteredEngins = filterListByQuery(engins, filterQuery, ['code', 'type', 'unite']);

  const columns = [
    { key: 'code', label: 'Code' },
    { key: 'famille.nom', label: 'Famille', render: (_: unknown, row: Engin) => row.famille?.nom ?? '' },
    { key: 'type', label: 'Type' },
    { key: 'unite', label: 'Unité' },
    { key: 'prixUnitaire', label: 'Prix unitaire' },
    { key: 'reservoir', label: 'Réservoir' },
    { key: 'consommation_max', label: 'Con. max' },
    { key: 'consommation_min', label: 'Con. min' },
    { key: 'current_cpt', label: 'Compteur' },
    {
      key: 'prisEnCharge',
      label: 'Pris en charge',
      render: (v: unknown) => (v ? 'Oui' : 'Non'),
    },
  ];

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditing(null);
    dispatch(fetchEngins(0));
  };

  const handleDeleteConfirm = () => {
    if (toDelete) {
      dispatch(deleteEngin(toDelete));
      setToDelete(null);
      setConfirmOpen(false);
      dispatch(fetchEngins(0));
    }
  };

  return (
    <Page title="Engins">
      <Tabs
        tabs={[
          { label: 'Engins', icon: <Car className="h-4 w-4" /> },
          { label: 'Familles', icon: <Layers className="h-4 w-4" /> },
          { label: 'Affectations', icon: <Link2 className="h-4 w-4" /> },
        ]}
        value={tab}
        onChange={setTab}
      />

      <TabPanel value={tab} index={0}>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">Liste des engins</h2>
              <FilterInput value={filterQuery} onChange={setFilterQuery} placeholder="Rechercher (code, type, unité)…" />
            </div>
            <AddButton label="Créer un engin" disabled={!canCreate} onClick={() => { setEditing(null); setModalOpen(true); }} />
          </div>
          <ListWithPagination
            page={pageNum}
            totalPages={totalPages}
            onPageChange={(p) => dispatch(fetchEngins(p))}
          >
            <DataTable<Engin>
              columns={columns}
              data={filteredEngins}
              keyField="id"
              emptyMessage="Aucun engin"
              actions={(row) => {
                const original = engins.find((e) => e.id === row.id);
                if (!original) return null;
                return (
                  <TableActions
                    onEdit={() => { setEditing(original); setModalOpen(true); }}
                    onDelete={() => { setToDelete(original); setConfirmOpen(true); }}
                    editDisabled={!canUpdate}
                    deleteDisabled={!canDelete}
                  />
                );
              }}
            />
          </ListWithPagination>
        </div>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <EnginFamilleTab />
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <EnginAffectationsSection onModalClose={() => dispatch(fetchEngins(0))} />
      </TabPanel>

      <Modal size="lg" open={modalOpen} onClose={handleCloseModal} title={editing ? 'Modifier l\'engin' : 'Nouvel engin'}>
        <EnginForm engin={editing ?? undefined} type={editing ? 'update' : 'create'} onClose={handleCloseModal} onSuccess={handleCloseModal} />
      </Modal>

      <ConfirmModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDeleteConfirm} message="Voulez-vous supprimer cet engin ?" />
    </Page>
  );
}
